"use client";

import { useScroll } from "motion/react";
import { useRef, type ReactNode } from "react";

interface StickyBeat {
  id: string;
  /** 0–1 within the scene where this beat is active. */
  start: number;
  end: number;
}

interface StickyScenePops {
  /** Total scrollable height of the scene. Use viewport-relative values like "300vh". */
  height?: string;
  /** Sticky top offset. Default: "0px" (header overlap handled by parent). */
  top?: string;
  /** Render-prop: receives scrollYProgress (0–1) + active beat index. */
  children: (state: { progress: import("motion/react").MotionValue<number> }) => ReactNode;
  className?: string;
  ariaLabel?: string;
}

/**
 * A sticky scroll scene. Outer wrapper provides scrollable height; inner pinned
 * content uses the render-prop to receive a MotionValue tracking 0–1 progress
 * through the scene. Compose with `useTransform` to drive any animated state.
 *
 * Use for one signature scene per page (e.g., the architecture story).
 * Mobile fallback: parent should switch to non-sticky stack at <md breakpoints.
 */
export default function StickyScene({
  height = "300vh",
  top = "0px",
  children,
  className = "",
  ariaLabel,
}: StickyScenePops) {
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end end"],
  });

  return (
    <div
      ref={ref}
      className={`relative ${className}`}
      style={{ height }}
      aria-label={ariaLabel}
    >
      <div
        className="sticky h-screen w-full overflow-hidden"
        style={{ top }}
      >
        {children({ progress: scrollYProgress })}
      </div>
    </div>
  );
}

export type { StickyBeat };
